// PDF ダウンロード時のファイル名を、キャンバス名から安全に組み立てる。
// OS のファイル名として使えない文字（\ / : * ? " < > | と制御文字）を "_" へ置換し、
// 末尾へ拡張子 .pdf を付与する。空になった場合は既定名でフォールバックする。

const DEFAULT_BASE_NAME = "canvas";

// ファイル名として無効な文字（Windows で不可の 9 文字）と制御文字（U+0000〜U+001F）。
const INVALID_FILENAME_CHARS = /[\\/:*?"<>|\x00-\x1f]/g;

// Windows の予約デバイス名（拡張子の有無に関わらず不可）。大文字小文字は区別しない。
// Windows は "CON" だけでなく "CON.txt" のように拡張子付きも予約扱いで保存できないため、
// 判定は最初の "." より前のデバイス名部分（deviceBase）で行う。
const WINDOWS_RESERVED_NAMES = new Set([
  "con",
  "prn",
  "aux",
  "nul",
  ...Array.from({ length: 9 }, (_, i) => `com${i + 1}`),
  ...Array.from({ length: 9 }, (_, i) => `lpt${i + 1}`),
]);

// ベース名（拡張子を除く部分）の最大 UTF-8 バイト長。多くのファイルシステムは名前長を
// バイト数で制限（255 バイト等）するため、文字数ではなく UTF-8 バイト長で丸める
// （日本語は 1 文字 3〜4 バイトになり、文字数基準だとバイト長超過を見逃すため）。".pdf" を加えても安全側。
const MAX_BASE_BYTES = 200;

/** value を UTF-8 バイト長が maxBytes 以内へ収まるよう末尾から切り詰める（末尾の空白・ドットは除く）。 */
function truncateBaseNameByUtf8Bytes(value: string, maxBytes: number): string {
  const encoder = new TextEncoder();
  // Array.from はコードポイント単位で分割する（サロゲート対を 1 要素として扱う）。
  // UTF-16 コードユニット単位で削るとサロゲート対を途中で割り、末尾に孤立 high surrogate が
  // 残って "�" 化・保存名不一致を招くため、コードポイント単位で 1 文字ずつ縮める。
  const codePoints = Array.from(value);
  while (codePoints.length > 0 && encoder.encode(codePoints.join("")).length > maxBytes) {
    codePoints.pop();
  }
  return codePoints.join("").replace(/[\s.]+$/g, "");
}

/** ファイル名に使えない文字を "_" へ置換し、前後の空白・ドットを整理する。 */
export function buildPdfDownloadFileName(title: string): string {
  const sanitized = title
    .replace(INVALID_FILENAME_CHARS, "_")
    // 連続する空白を 1 つへ畳み込む。
    .replace(/\s+/g, " ")
    // 前後の空白・ピリオドを取り除く（末尾ピリオドは Windows でファイル名として不可）。
    .replace(/^[\s.]+|[\s.]+$/g, "");
  let base = sanitized.length > 0 ? sanitized : DEFAULT_BASE_NAME;
  // 長すぎるベース名は UTF-8 バイト長で丸める（丸めた結果に末尾空白・ドットが残らないよう再度整理）。
  if (new TextEncoder().encode(base).length > MAX_BASE_BYTES) {
    base = truncateBaseNameByUtf8Bytes(base, MAX_BASE_BYTES);
    if (base.length === 0) base = DEFAULT_BASE_NAME;
  }
  // 予約デバイス名（CON / PRN / AUX / NUL / COM1-9 / LPT1-9）と一致する場合は先頭へ "_" を付けて回避する。
  // "CON.txt" のような拡張子付きも予約扱いのため、最初の "." より前のデバイス名部分で判定する。
  const deviceBase = (base.split(".")[0] ?? base).toLowerCase();
  if (WINDOWS_RESERVED_NAMES.has(deviceBase)) {
    base = `_${base}`;
  }
  return `${base}.pdf`;
}
